import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Sparkles, CheckCircle2, ChevronDown, ChevronUp, RotateCcw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { MenuMatchEntryRow } from "@/components/inventory/MenuMatchEntryRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_TYPE_LABELS = { lunch: "Lunch", breakfast_snack: "B/S" };

const TimesServedInput = ({ entry, menuType, month, year, authHeaders, onUpdated }) => {
  const [value, setValue] = useState(entry.timesServed?.[menuType] ?? "");
  const [saving, setSaving] = useState(false);
  const flagged = entry.timesServed && Object.prototype.hasOwnProperty.call(entry.timesServed, menuType)
    && entry.timesServed[menuType] === null;

  useEffect(() => { setValue(entry.timesServed?.[menuType] ?? ""); }, [entry, menuType]);

  const handleBlur = async () => {
    const num = value === "" ? null : parseInt(value, 10);
    if (num === (entry.timesServed?.[menuType] ?? null)) return; // unchanged, skip save
    setSaving(true);
    try {
      const res = await axios.put(`${API}/inventory/menu/matches/times-served`, {
        rawText: entry.rawText, menuType, value: num,
      }, { params: { month, year }, headers: authHeaders });
      onUpdated(res.data.match_record);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save Times Served");
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1 text-xs" data-testid={`times-served-${entry.rawText}-${menuType}`}>
      {MENU_TYPE_LABELS[menuType]}:
      <Input type="number" min="0" value={value} disabled={saving}
        placeholder={flagged ? "?" : "0"}
        onChange={(e) => setValue(e.target.value)} onBlur={handleBlur}
        className="h-6 w-12 px-1 text-xs" data-testid={`times-served-input-${entry.rawText}-${menuType}`} />
      {flagged && <AlertTriangle className="w-3 h-3 text-amber-600" title="Couldn't read this from the menu — please confirm" />}
    </span>
  );
};

export const MatchToMenuPanel = ({ month, year, authHeaders, items, onItemsChanged, storageAreas, onManageItems }) => {
  const [menuSource, setMenuSource] = useState(null);
  const [loadingSource, setLoadingSource] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [matchRecord, setMatchRecord] = useState(null);
  const [scanUsage, setScanUsage] = useState(null);
  const [confirmedOpen, setConfirmedOpen] = useState(false);
  const [unresolvingRawText, setUnresolvingRawText] = useState(null);
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [needsReviewSearch, setNeedsReviewSearch] = useState("");

  const checkScanUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory/scan-usage`, { headers: authHeaders });
      setScanUsage(res.data);
    } catch {
      setScanUsage(null);
    }
  }, [authHeaders]);

  const checkSource = useCallback(async () => {
    setLoadingSource(true);
    try {
      const res = await axios.get(`${API}/inventory/menu-source`, { params: { month, year }, headers: authHeaders });
      setMenuSource(res.data);
    } catch (err) {
      toast.error("Could not check menu source");
    } finally {
      setLoadingSource(false);
    }
  }, [month, year, authHeaders]);

  const checkMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory/menu/matches`, { params: { month, year }, headers: authHeaders });
      setMatchRecord(res.data.match_record);
    } catch {
      setMatchRecord(null);
    }
  }, [month, year, authHeaders]);

  useEffect(() => { checkSource(); checkMatches(); checkScanUsage(); }, [checkSource, checkMatches, checkScanUsage]);

  const handleScanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("month", month);
      formData.append("year", year);
      await axios.post(`${API}/inventory/scan-menu/upload`, formData, { headers: authHeaders });
      toast.success("Menu uploaded");
      checkSource();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/inventory/menu/extract`, null, { params: { month, year }, headers: authHeaders });
      setMatchRecord(res.data.match_record);
      if (res.data.scanUsage) setScanUsage({ ...scanUsage, ...res.data.scanUsage });
      toast.success("Menu items extracted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const handleResolved = (updatedRecord) => {
    setMatchRecord(updatedRecord);
    onItemsChanged();
  };

  const handleUnresolve = async (rawText) => {
    setUnresolvingRawText(rawText);
    try {
      const res = await axios.post(`${API}/inventory/menu/matches/resolve`, {
        rawText, action: "unresolve",
      }, { params: { month, year }, headers: authHeaders });
      toast.success("Moved back to Needs Review");
      handleResolved(res.data.match_record);
      await checkMatches(); // re-fetch fresh from the server so the list never shows a stale state
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move this back to Needs Review");
    } finally {
      setUnresolvingRawText(null);
    }
  };

  if (loadingSource) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  const needsReview = matchRecord?.entries?.filter((e) => e.status === "needs_review") || [];
  const resolvedOrMatched = matchRecord?.entries?.filter((e) => e.status !== "needs_review") || [];

  const confirmedNameOf = (entry) => {
    const matched = entry.matchedItemIds
      .map((id) => items.find((i) => i.id === id)?.canonicalName)
      .filter(Boolean);
    return matched.length === 1 ? matched[0] : entry.rawText;
  };
  const confirmedSearchTextOf = (entry) => {
    const matched = entry.matchedItemIds
      .map((id) => items.find((i) => i.id === id)?.canonicalName)
      .filter(Boolean);
    return [entry.rawText, ...matched].join(" ");
  };
  const confirmedList = resolvedOrMatched
    .filter((entry) => confirmedSearchTextOf(entry).toLowerCase().includes(confirmedSearch.trim().toLowerCase()))
    .sort((a, b) => confirmedNameOf(a).localeCompare(confirmedNameOf(b)));
  const needsReviewList = needsReview
    .filter((entry) => entry.rawText.toLowerCase().includes(needsReviewSearch.trim().toLowerCase()))
    .sort((a, b) => a.rawText.localeCompare(b.rawText));

  return (
    <div data-testid="match-to-menu-panel" className="space-y-5">
      {menuSource?.source === "center_menu" && (
        <div className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-800">
          <CheckCircle2 className="w-4 h-4" /> Menu found in Center Menu for this month.
        </div>
      )}
      {menuSource?.source === "scanned_menu" && (
        <div className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-800">
          <CheckCircle2 className="w-4 h-4" /> Scanned menu on file: {menuSource.menu?.filename}
        </div>
      )}

      {/* Manual upload sits alongside whatever menu source is already on file — it doesn't
          replace relying on Center Menu, it's here for centers that don't want to. */}
      <div className="space-y-2">
        <p className="text-sm text-slate-600">
          {menuSource?.source === "none"
            ? "No menu on file for this month. Not using Center Menu? Upload one here."
            : "Have a different document to read instead? Upload it here."}
        </p>
        <label data-testid="scan-menu-upload-label" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-slate-300 text-sm text-slate-600 cursor-pointer hover:border-[#2F6DB5]">
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading..." : "Scan Menu"}
          <input type="file" accept=".xlsx,.png,.jpg,.jpeg,.pdf" className="hidden" data-testid="scan-menu-file-input"
            onChange={handleScanUpload} disabled={uploading} />
        </label>
        <p className="text-xs text-slate-400" data-testid="scan-menu-accepted-formats">
          Accepted formats: Excel (.xlsx), PDF, JPG, and PNG.
        </p>
      </div>

      {menuSource?.source !== "none" && (
        <div className="flex items-center gap-3">
          <Button data-testid="extract-menu-items-button" onClick={handleExtract} disabled={extracting || (scanUsage?.remaining === 0)}>
            {extracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {extracting ? "Reading your menu…" : matchRecord ? "Re-extract Items" : "Extract Items from Menu"}
          </Button>
          {scanUsage && (
            <span data-testid="scan-usage-indicator" className="text-xs text-slate-500">
              {scanUsage.limit === null
                ? "Unlimited scans."
                : scanUsage.tier === "trial"
                ? `${scanUsage.note || "Your trial includes 1 menu scan and 1 receipt/invoice scan."} Menu scan ${scanUsage.remaining > 0 ? "not used yet" : "already used"}.`
                : `${scanUsage.remaining} of ${scanUsage.limit} combined menu/receipt scans left this month`}
            </span>
          )}
        </div>
      )}

      {matchRecord && (
        <div className="space-y-4">
          {onManageItems && (
            <button type="button" data-testid="go-to-manage-items-link" onClick={onManageItems}
              className="text-sm text-[#2F6DB5] hover:underline font-medium">
              Fix a typo or merge duplicate ingredients →
            </button>
          )}
          {resolvedOrMatched.length > 0 && (
            <div className="border border-slate-200 rounded-lg">
              <button type="button" data-testid="confirmed-section-toggle"
                onClick={() => setConfirmedOpen((o) => !o)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-slate-700">
                <span>Confirmed ({resolvedOrMatched.length})</span>
                {confirmedOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {confirmedOpen && (
                <div className="px-3 pb-3 space-y-2">
                  <Input data-testid="confirmed-search-input" value={confirmedSearch}
                    onChange={(e) => setConfirmedSearch(e.target.value)}
                    placeholder="Search confirmed items…" className="h-8 text-xs" />
                  <div className="space-y-1.5">
                    {confirmedList.map((entry) => {
                      const matchedNames = entry.matchedItemIds
                        .map((id) => items.find((i) => i.id === id)?.canonicalName)
                        .filter(Boolean);
                      // Split dishes (multiple linked items) keep the original dish name visible so it
                      // doesn't just vanish from this list — shown as "Dish" → ingredients instead.
                      const label = matchedNames.length > 1
                        ? `"${entry.rawText}" → ${matchedNames.join(", ")}`
                        : matchedNames.length === 1 ? matchedNames[0] : entry.rawText;
                      return (
                        <div key={entry.rawText} data-testid={`confirmed-entry-${entry.rawText}`}
                          className="flex flex-wrap items-center gap-2 text-xs pl-2.5 pr-1.5 py-1.5 rounded-lg bg-slate-100 text-slate-600">
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" /> {label}
                          </span>
                          <button type="button" data-testid={`unresolve-${entry.rawText}`}
                            title="Move back to Needs Review" disabled={unresolvingRawText === entry.rawText}
                            onClick={() => handleUnresolve(entry.rawText)}
                            className="p-0.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 disabled:opacity-50">
                            <RotateCcw className="w-3 h-3" />
                          </button>
                          <span className="text-slate-400">Times Served:</span>
                          {(entry.sourceMenuTypes || []).map((mt) => (
                            <TimesServedInput key={mt} entry={entry} menuType={mt} month={month} year={year}
                              authHeaders={authHeaders} onUpdated={handleResolved} />
                          ))}
                        </div>
                      );
                    })}
                    {confirmedList.length === 0 && (
                      <p className="text-xs text-slate-400 py-1.5">No confirmed items match your search.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {needsReview.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">
                Needs Review ({needsReview.length})
              </h4>
              <Input data-testid="needs-review-search-input" value={needsReviewSearch}
                onChange={(e) => setNeedsReviewSearch(e.target.value)}
                placeholder="Search needs review…" className="h-8 text-xs mb-2" />
              <div className="space-y-2">
                {needsReviewList.map((entry) => (
                  <MenuMatchEntryRow key={entry.rawText} entry={entry} items={items} storageAreas={storageAreas}
                    month={month} year={year} authHeaders={authHeaders} onResolved={handleResolved} />
                ))}
                {needsReviewList.length === 0 && (
                  <p className="text-xs text-slate-400 py-1.5">No items in Needs Review match your search.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchToMenuPanel;
